import type { Lesson } from "./types";

export const lessons: Lesson[] = [
	{
		id: "lesson-1",
		title: "Gravity & Normal Force",
		description: "Learn to identify the two most fundamental forces: gravity pulling objects down and the normal force pushing back from surfaces.",
		icon: "weight",
		concepts: [
			"Every object with mass experiences gravitational force (weight) pulling it toward Earth's center.",
			"When an object rests on a surface, the surface pushes back with a normal force perpendicular to the surface.",
			"On a flat surface at rest, the normal force equals the gravitational force in magnitude.",
		],
		scenarios: [
			{
				id: "1-1",
				title: "Book on a Table",
				description: "A book sits motionless on a flat table. Identify all forces acting on the book.",
				hint: "Think about what pulls the book down and what stops it from falling through the table.",
				environment: "flat",
				objectShape: "box",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force (weight) pulling the book toward Earth" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "medium", description: "Normal force from the table pushing up on the book" },
				],
				explanation: "The book has two forces: gravity (Fg) pulling it downward, and the normal force (FN) from the table pushing it upward. Since the book is at rest, these forces are equal in magnitude and opposite in direction, resulting in zero net force.",
				difficulty: 1,
			},
			{
				id: "1-2",
				title: "Ball in Free Fall",
				description: "A ball has just been dropped and is falling through the air (ignore air resistance). What force(s) act on it?",
				hint: "With no surface contact and ignoring air resistance, what's the only force?",
				environment: "projectile",
				objectShape: "circle",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the ball toward Earth" },
				],
				explanation: "In free fall (ignoring air resistance), the only force acting on the ball is gravity. There is no surface contact, so there is no normal force. This is why the ball accelerates downward at g = 9.8 m/s\u00B2.",
				difficulty: 1,
			},
			{
				id: "1-3",
				title: "Person Standing on Floor",
				description: "A person stands still on a level floor. Identify all forces acting on the person.",
				hint: "This is similar to the book on the table \u2014 just a different object.",
				environment: "flat",
				objectShape: "person",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "large", description: "Gravitational force (weight) pulling the person toward Earth" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "large", description: "Normal force from the floor pushing up on the person" },
				],
				explanation: "Just like the book, the person experiences gravity pulling down and the normal force from the floor pushing up. The person is in static equilibrium \u2014 net force is zero.",
				difficulty: 1,
			},
		],
	},
	{
		id: "lesson-2",
		title: "Friction Forces",
		description: "Understand how friction opposes motion and learn to include it in your free-body diagrams.",
		icon: "friction",
		concepts: [
			"Friction is a force that opposes the relative motion (or attempted motion) between two surfaces in contact.",
			"Static friction prevents an object from starting to move. Kinetic friction acts on an object already sliding.",
			"Friction always acts parallel to the contact surface and opposite to the direction of motion or attempted motion.",
		],
		scenarios: [
			{
				id: "2-1",
				title: "Box Pushed Across Floor",
				description: "A box is being pushed to the right across a rough floor at constant velocity. Identify all forces.",
				hint: "There are four forces: the push, friction opposing motion, gravity, and the normal force.",
				environment: "flat",
				objectShape: "box",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "medium", description: "Normal force from the floor" },
					{ type: "applied", label: "Fapp", direction: "right", magnitude: "medium", description: "Applied force pushing the box to the right" },
					{ type: "friction", label: "Ff", direction: "left", magnitude: "medium", description: "Kinetic friction opposing the box's rightward motion" },
				],
				explanation: "Four forces act on the box: gravity down, normal force up, applied force to the right, and kinetic friction to the left. Since the box moves at constant velocity, the net force is zero \u2014 the applied force equals friction, and normal force equals gravity.",
				difficulty: 2,
			},
			{
				id: "2-2",
				title: "Stationary Box with Applied Force",
				description: "Someone pushes a heavy box to the right, but it doesn't move. Identify all forces acting on the box.",
				hint: "Static friction is preventing the box from moving. It matches the applied force.",
				environment: "flat",
				objectShape: "box",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "large", description: "Gravitational force pulling the heavy box down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "large", description: "Normal force from the floor" },
					{ type: "applied", label: "Fapp", direction: "right", magnitude: "small", description: "Applied force pushing to the right (not enough to move it)" },
					{ type: "friction", label: "Fs", direction: "left", magnitude: "small", description: "Static friction opposing the attempted motion" },
				],
				explanation: "Even though the box doesn't move, friction is present! Static friction exactly matches the applied force to keep the box stationary. Gravity and normal force balance vertically. All four forces sum to zero net force.",
				difficulty: 2,
			},
		],
	},
	{
		id: "lesson-3",
		title: "Inclined Planes (Ramps)",
		description: "Master the challenge of forces on ramps, where gravity has components both along and perpendicular to the surface.",
		icon: "ramp",
		concepts: [
			"On a ramp, gravity still pulls straight down, but it's useful to decompose it into components parallel and perpendicular to the surface.",
			"The component parallel to the ramp (mg sin \u03B8) tends to pull the object down the slope.",
			"The normal force is perpendicular to the ramp surface, not straight up.",
		],
		scenarios: [
			{
				id: "3-1",
				title: "Box on a Frictionless Ramp",
				description: "A box sits on a smooth (frictionless) ramp inclined at 30\u00B0. It slides down the ramp. Identify all forces.",
				hint: "On a frictionless ramp, only gravity and the normal force act. The normal force is perpendicular to the ramp surface.",
				environment: "ramp",
				objectShape: "box",
				rampAngle: 30,
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box straight down" },
					{ type: "normal", label: "FN", direction: "up-left", magnitude: "medium", description: "Normal force perpendicular to the ramp surface" },
				],
				explanation: "Only two forces act: gravity pulling straight down and the normal force perpendicular to the ramp surface. Since there's no friction, the component of gravity along the ramp (mg sin 30\u00B0) causes the box to accelerate down the slope.",
				difficulty: 2,
			},
			{
				id: "3-2",
				title: "Box on a Rough Ramp (Stationary)",
				description: "A box sits motionless on a rough ramp inclined at 25\u00B0. Friction keeps it from sliding. Identify all forces.",
				hint: "Three forces: gravity down, normal force perpendicular to ramp, and static friction up the ramp.",
				environment: "ramp",
				objectShape: "box",
				rampAngle: 25,
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box straight down" },
					{ type: "normal", label: "FN", direction: "up-left", magnitude: "medium", description: "Normal force perpendicular to the ramp surface" },
					{ type: "friction", label: "Fs", direction: "up-right", magnitude: "small", description: "Static friction preventing the box from sliding down the ramp" },
				],
				explanation: "Three forces act on the box: gravity (straight down), normal force (perpendicular to the ramp), and static friction (up the ramp surface). The friction force balances the component of gravity parallel to the ramp, keeping the box stationary.",
				difficulty: 2,
			},
			{
				id: "3-3",
				title: "Box Pushed Up a Ramp",
				description: "A box is being pushed up a rough ramp at constant speed. Identify all forces acting on the box.",
				hint: "Four forces: gravity, normal, friction (opposing upward motion, so pointing down the ramp), and the applied push up the ramp.",
				environment: "ramp",
				objectShape: "box",
				rampAngle: 30,
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box straight down" },
					{ type: "normal", label: "FN", direction: "up-left", magnitude: "medium", description: "Normal force perpendicular to the ramp surface" },
					{ type: "friction", label: "Fk", direction: "down-left", magnitude: "small", description: "Kinetic friction opposing the upward motion (points down the ramp)" },
					{ type: "applied", label: "Fapp", direction: "up-right", magnitude: "medium", description: "Applied force pushing the box up the ramp" },
				],
				explanation: "Four forces: gravity (down), normal force (perpendicular to ramp), kinetic friction (down the ramp, opposing the upward motion), and the applied force (up the ramp). At constant speed, the net force is zero.",
				difficulty: 3,
			},
		],
	},
	{
		id: "lesson-4",
		title: "Tension & Hanging Objects",
		description: "Explore tension forces in ropes, strings, and cables supporting objects.",
		icon: "tension",
		concepts: [
			"Tension is the pulling force transmitted through a rope, string, or cable when it is pulled taut.",
			"Tension always pulls along the direction of the rope, away from the object.",
			"For an object hanging at rest from a single rope, the tension equals the weight of the object.",
		],
		scenarios: [
			{
				id: "4-1",
				title: "Hanging Lamp",
				description: "A lamp hangs motionless from a single cable attached to the ceiling. Identify all forces.",
				hint: "Only two forces: gravity pulling down and the cable tension pulling up.",
				environment: "hanging",
				objectShape: "circle",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the lamp downward" },
					{ type: "tension", label: "FT", direction: "up", magnitude: "medium", description: "Tension in the cable pulling the lamp upward" },
				],
				explanation: "Two forces act on the lamp: gravity pulling it down and tension in the cable pulling it up. Since the lamp is at rest, these two forces are equal and opposite.",
				difficulty: 1,
			},
			{
				id: "4-2",
				title: "Tug of War (Rope Under Tension)",
				description: "A box is pulled to the right by a rope while friction opposes the motion. The box moves at constant velocity. Identify all forces on the box.",
				hint: "Four forces: gravity, normal, tension from the rope (right), and friction (left).",
				environment: "flat",
				objectShape: "box",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "medium", description: "Normal force from the surface" },
					{ type: "tension", label: "FT", direction: "right", magnitude: "medium", description: "Tension in the rope pulling the box to the right" },
					{ type: "friction", label: "Ff", direction: "left", magnitude: "medium", description: "Kinetic friction opposing the rightward motion" },
				],
				explanation: "Four forces: gravity (down), normal force (up), tension from the rope (right), and kinetic friction (left). At constant velocity, the net force is zero \u2014 tension equals friction, and the normal force equals gravity.",
				difficulty: 2,
			},
		],
	},
	{
		id: "lesson-5",
		title: "Elevators & Apparent Weight",
		description: "Analyze forces when objects accelerate vertically, like in elevators, and understand how acceleration changes the normal force.",
		icon: "elevator",
		concepts: [
			"In an elevator accelerating upward, the normal force is greater than gravity \u2014 you feel heavier.",
			"In an elevator accelerating downward, the normal force is less than gravity \u2014 you feel lighter.",
			"At constant velocity (even if moving), the forces balance just like when stationary.",
		],
		scenarios: [
			{
				id: "5-1",
				title: "Elevator at Constant Speed",
				description: "A person rides in an elevator moving upward at constant speed. Identify all forces on the person.",
				hint: "Constant speed means zero acceleration. The forces balance just like standing on the ground.",
				environment: "elevator",
				objectShape: "person",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "large", description: "Gravitational force pulling the person down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "large", description: "Normal force from the elevator floor (equals weight)" },
				],
				explanation: "Even though the elevator is moving upward, it's at constant speed \u2014 meaning zero acceleration. The normal force equals the gravitational force, just like standing on the ground. You would feel your normal weight.",
				difficulty: 1,
			},
			{
				id: "5-2",
				title: "Elevator Accelerating Upward",
				description: "A person stands in an elevator that is accelerating upward. Identify all forces on the person. Note that the normal force must be larger than gravity to produce upward acceleration.",
				hint: "Same two forces as usual, but the normal force must be larger than gravity to produce upward acceleration.",
				environment: "elevator",
				objectShape: "person",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the person down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "large", description: "Normal force from the elevator floor (greater than weight due to upward acceleration)" },
				],
				explanation: "The person experiences gravity (down) and the normal force (up). Since the elevator accelerates upward, the normal force must exceed gravity to produce a net upward force (F_net = FN - Fg = ma). This is why you feel heavier when an elevator starts going up.",
				difficulty: 2,
			},
			{
				id: "5-3",
				title: "Elevator Accelerating Downward",
				description: "A person stands in an elevator that is accelerating downward. Identify all forces on the person. The normal force is smaller than gravity here.",
				hint: "The normal force is less than gravity because the net force must point downward.",
				environment: "elevator",
				objectShape: "person",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "large", description: "Gravitational force pulling the person down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "medium", description: "Normal force from the elevator floor (less than weight due to downward acceleration)" },
				],
				explanation: "The person experiences gravity (down) and the normal force (up). Since the elevator accelerates downward, the normal force must be less than gravity to produce a net downward force. This is why you feel lighter when an elevator starts going down. (In free fall, FN = 0 and you'd feel weightless!)",
				difficulty: 2,
			},
		],
	},
	{
		id: "lesson-6",
		title: "Combined Challenges",
		description: "Put it all together with complex scenarios involving multiple force types in varied environments.",
		icon: "challenge",
		concepts: [
			"Real-world situations often combine multiple force types: gravity, normal, friction, tension, and applied forces.",
			"The key to drawing correct free-body diagrams is systematically checking for each possible force.",
			"Always ask: Is there gravity? Is there surface contact (normal force)? Is there friction? Are there ropes/cables (tension)? Is there a push or pull (applied force)?",
		],
		scenarios: [
			{
				id: "6-1",
				title: "Box Pulled Up by Rope on Ramp",
				description: "A box on a rough ramp is pulled upward along the ramp by a rope at constant speed. Identify all forces.",
				hint: "Five key forces: gravity, normal (perpendicular to ramp), friction (down the ramp), and tension (up the ramp).",
				environment: "ramp",
				objectShape: "box",
				rampAngle: 35,
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the box straight down" },
					{ type: "normal", label: "FN", direction: "up-left", magnitude: "medium", description: "Normal force perpendicular to the ramp" },
					{ type: "friction", label: "Fk", direction: "down-left", magnitude: "small", description: "Kinetic friction opposing upward motion (points down ramp)" },
					{ type: "tension", label: "FT", direction: "up-right", magnitude: "medium", description: "Tension in the rope pulling the box up the ramp" },
				],
				explanation: "Four forces act: gravity (straight down), normal force (perpendicular to ramp surface), kinetic friction (down the ramp, opposing the upward motion), and tension in the rope (up the ramp). At constant speed, the net force is zero.",
				difficulty: 3,
			},
			{
				id: "6-2",
				title: "Hanging Object with Air Resistance",
				description: "A ball attached to a string falls through the air. The string has snapped and is no longer attached. Air resistance opposes its fall. Identify all forces on the ball.",
				hint: "Two forces: gravity pulling down and air resistance pushing up (opposing the downward motion).",
				environment: "projectile",
				objectShape: "circle",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "medium", description: "Gravitational force pulling the ball down" },
					{ type: "air-resistance", label: "Fair", direction: "up", magnitude: "small", description: "Air resistance opposing the downward motion" },
				],
				explanation: "Two forces act: gravity pulling the ball down and air resistance pushing up (opposing the direction of motion). The net force is downward (gravity > air resistance), so the ball accelerates downward, but more slowly than in a vacuum.",
				difficulty: 2,
			},
			{
				id: "6-3",
				title: "Person in Decelerating Elevator",
				description: "A person holding a suitcase stands in an elevator that is moving upward but slowing down (decelerating). Identify the forces on the person.",
				hint: "Moving up but decelerating means the acceleration is downward. The normal force will be less than gravity.",
				environment: "elevator",
				objectShape: "person",
				correctForces: [
					{ type: "gravity", label: "Fg", direction: "down", magnitude: "large", description: "Gravitational force pulling the person down" },
					{ type: "normal", label: "FN", direction: "up", magnitude: "medium", description: "Normal force from elevator floor (less than weight because of downward acceleration)" },
				],
				explanation: "Moving upward but decelerating means the acceleration is downward. Two forces act: gravity (down) and normal force (up). Since the acceleration is downward, FN < Fg. This is equivalent to the elevator-accelerating-downward scenario \u2014 the direction of acceleration matters, not the direction of motion!",
				difficulty: 3,
			},
		],
	},
];
